import { useCallback } from 'react'
import log from 'loglevel'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { ControlServicePromiseClient } from '@trustero/trustero-api-web/lib/model/control_grpc_web_pb'
import {
  CONTROL_STATUS,
  ControlStatusValue,
  UpdateControlFieldsRequest,
  UpdateControlSuggestedEvidenceRequest,
  UpdateControlSuggestedTestsRequest,
  RegenerateControlSuggestionRequest,
  RegenerateControlOperationalRequest,
  UpdateControlPolicyRequest,
  UpdateControlFrameworksRequest,
  RefreshControlOperationalRequest,
  UpdateControlObjectiveRequest,
  UpdateControlProcedureRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import {
  RESPONSIBILITY,
  ResponsibilityValue,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import { useAuthorizedGrpcClient } from '../../../../adapter'
import { useInvalidateControlsCache } from './useControls'

export const useUpdateControlFields = (): ((args: {
  controlId: string
  objective?: string
  procedure?: string
  note?: string
  status?: CONTROL_STATUS
  ownerEmail?: string
  responsibility?: RESPONSIBILITY
  notApplicableReason?: string
  vendorId?: string
  name?: string
  modelId?: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({
      controlId,
      objective,
      procedure,
      note,
      status,
      ownerEmail,
      responsibility,
      notApplicableReason,
      vendorId,
      name,
      modelId,
    }) => {
      try {
        const request = new UpdateControlFieldsRequest().setControlId(controlId)
        if (objective !== undefined) {
          request.setObjective(new StringValue().setValue(objective))
        }
        if (procedure !== undefined) {
          request.setProcedure(new StringValue().setValue(procedure))
        }
        if (note !== undefined) {
          request.setNote(new StringValue().setValue(note))
        }
        if (status !== undefined) {
          request.setStatus(new ControlStatusValue().setValue(status))
        }
        if (ownerEmail !== undefined) {
          request.setOwnerEmail(new StringValue().setValue(ownerEmail))
        }
        if (responsibility !== undefined) {
          request.setResponsibility(
            new ResponsibilityValue().setValue(responsibility),
          )
        }
        if (notApplicableReason !== undefined) {
          request.setNotApplicableReason(
            new StringValue().setValue(notApplicableReason),
          )
        }
        if (vendorId !== undefined) {
          request.setVendorId(new StringValue().setValue(vendorId))
        }
        if (name !== undefined) {
          request.setName(new StringValue().setValue(name))
        }
        if (modelId !== undefined) {
          request.setModelId(new StringValue().setValue(modelId))
        }
        await client.updateControlFields(request)
      } catch (error) {
        log.error('Error updating control fields', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlSuggestedEvidence = (): ((args: {
  controlId: string
  suggestedEvidence: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, suggestedEvidence }) => {
      try {
        const request = new UpdateControlSuggestedEvidenceRequest()
          .setControlId(controlId)
          .setSuggestedEvidence(suggestedEvidence)
        await client.updateControlSuggestedEvidence(request)
      } catch (error) {
        log.error('Error updating suggested evidence', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlSuggestedTests = (): ((args: {
  controlId: string
  suggestedTests: string
  testProcedureId?: string
  title?: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, suggestedTests, testProcedureId, title }) => {
      try {
        const request = new UpdateControlSuggestedTestsRequest()
          .setControlId(controlId)
          .setSuggestedTests(suggestedTests)
        if (testProcedureId) {
          request.setTestProcedureId(
            new StringValue().setValue(testProcedureId),
          )
        }
        if (title !== undefined) {
          request.setTitle(new StringValue().setValue(title))
        }
        await client.updateControlSuggestedTests(request)
      } catch (error) {
        log.error('Error updating suggested tests', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useRegenerateControlSuggestedEvidence = (): ((
  controlId: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async (controlId: string) => {
      try {
        const request = new RegenerateControlSuggestionRequest().setControlId(
          controlId,
        )
        await client.regenerateControlSuggestedEvidence(request)
      } catch (error) {
        log.error('Error regenerating suggested evidence', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useRegenerateControlSuggestedTests = (): ((
  controlId: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async (controlId: string) => {
      try {
        const request = new RegenerateControlSuggestionRequest().setControlId(
          controlId,
        )
        await client.regenerateControlSuggestedTests(request)
      } catch (error) {
        log.error('Error regenerating suggested tests', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useRegenerateControlSuggestedOperational = (): ((
  controlId: string,
  complianceFrameworkBaseModelIds: string[],
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async (
      controlId: string,
      complianceFrameworkBaseModelIds: string[],
    ) => {
      try {
        const request = new RegenerateControlOperationalRequest()
          .setControlId(controlId)
          .setComplianceFrameworkBaseModelIdsList(
            complianceFrameworkBaseModelIds,
          )
        await client.regenerateControlSuggestedOperational(request)
      } catch (error) {
        log.error('Error regenerating suggested operational', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlPolicy = (): ((args: {
  controlId: string
  policyModelId?: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, policyModelId }) => {
      try {
        const request = new UpdateControlPolicyRequest().setControlId(controlId)
        if (policyModelId !== undefined) {
          request.setPolicyModelId(new StringValue().setValue(policyModelId))
        }
        await client.updateControlPolicy(request)
      } catch (error) {
        log.error('Error updating control policy', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlFrameworks = (): ((args: {
  controlId: string
  frameworkIds: string[]
  complianceFrameworkIds: string[]
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, frameworkIds, complianceFrameworkIds }) => {
      try {
        const request = new UpdateControlFrameworksRequest()
          .setControlId(controlId)
          .setFrameworkIdsList(frameworkIds)
          .setComplianceFrameworkIdsList(complianceFrameworkIds)
        await client.updateControlFrameworks(request)
      } catch (error) {
        log.error('Error updating control frameworks', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useRefreshControlSuggestedOperational = (): ((
  controlId: string,
  complianceFrameworkBaseModelIds: string[],
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async (
      controlId: string,
      complianceFrameworkBaseModelIds: string[],
    ) => {
      try {
        const request = new RefreshControlOperationalRequest()
          .setControlId(controlId)
          .setComplianceFrameworkBaseModelIdsList(
            complianceFrameworkBaseModelIds,
          )
        await client.refreshControlSuggestedOperational(request)
      } catch (error) {
        log.error('Error refreshing suggested operational', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlObjective = (): ((args: {
  controlId: string
  objective: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, objective }) => {
      try {
        const request = new UpdateControlObjectiveRequest()
          .setControlId(controlId)
          .setObjective(objective)
        await client.updateControlObjective(request)
      } catch (error) {
        log.error('Error updating control objective', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlProcedure = (): ((args: {
  controlId: string
  procedure: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, procedure }) => {
      try {
        const request = new UpdateControlProcedureRequest()
          .setControlId(controlId)
          .setProcedure(procedure)
        await client.updateControlProcedure(request)
      } catch (error) {
        log.error('Error updating control procedure', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}
